import React, { useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import SettingsLayout from '../../layout/dashboard/SettingsLayout';
import UsersList from '../../components/users/UsersList';
import OrgAIConfiguration from '../../components/ai-config/OrgAIConfiguration';
import RolesAndPermissions from '../../features/roles-permissions/RolesAndPermissions';
import { useUser } from '../../context/UserContext';
import { useAccess } from '../../hooks/useAccess';

const SETTING_VIEWS = {
  users: {
    title: 'Users',
    component: UsersList,
    policy: 'ManageUsers',
  },
  roles: {
    title: 'Roles & Permissions',
    component: RolesAndPermissions,
    policy: 'ManageRolesAndPermissions',
  },
  'ai-config': {
    title: 'AI Config',
    component: OrgAIConfiguration,
    policy: 'ManageAiConfigOrg',
  },
};

const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser: user } = useUser();
  const { canAccess, isReady } = useAccess();

  const view = searchParams.get('view');

  const availableViews = Object.keys(SETTING_VIEWS).filter((key) =>
    canAccess(SETTING_VIEWS[key].policy)
  );

  const defaultView = availableViews[0] || null;

  useEffect(() => {
    if (!isReady) return;
    if (!view) {
      if (defaultView) {
        navigate(`/settings?view=${defaultView}`, { replace: true });
      } else {
        navigate('/access-denied', { replace: true });
      }
    }
  }, [isReady, view, defaultView]);

  useEffect(() => {
    if (!isReady || !view) return;

    const config = SETTING_VIEWS[view];

    if (!config || !canAccess(config.policy)) {
      navigate('/access-denied', { replace: true });
    }
  }, [isReady, view]);

  if (!isReady) {
    return (
      <SettingsLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </SettingsLayout>
    );
  }

  const activeViewConfig = SETTING_VIEWS[view] || (defaultView ? SETTING_VIEWS[defaultView] : null);

  if (!activeViewConfig) {
    return null;
  }

  const ActiveComponent = activeViewConfig.component;

  return (
    <SettingsLayout>
      <Box>
        <ActiveComponent user={user} />
      </Box>
    </SettingsLayout>
  );
};

export default SettingsPage;